import {ReactNode} from 'react'

import {useUser} from '@hooks/use-user'

import {AuthenticatedLayout} from './AuthenticatedLayout'

interface ConditionalLayoutProps {
    children: ReactNode
    // Function or boolean to determine if authentication is required
    requireAuth: boolean | (() => boolean)
    // Whether to redirect to login when not authenticated (default: true)
    fallbackToLogin?: boolean
    // Whether to use minimal layout instead of full authenticated layout (default: false)
    minimalLayout?: boolean
    // Custom loading content
    loadingContent?: ReactNode
    // Custom not found content
    notFoundContent?: ReactNode
    // External loading state
    isLoading?: boolean
    // External not found state
    isNotFound?: boolean
}

const DefaultLoadingContent = () => (
    <div className="flex items-center justify-center h-screen">
        <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
        </div>
    </div>
)

const DefaultNotFoundContent = () => (
    <div className="flex items-center justify-center h-screen">
        <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Not Found</h1>
            <p>The requested resource could not be found or is not available.</p>
        </div>
    </div>
)

const MinimalLayout = ({children}: { children: ReactNode }) => (
    <div className="h-screen antialiased bg-muted/30 flex flex-col font-inter">
        <main className="flex flex-col h-full">
            {children}
        </main>
    </div>
)

export function ConditionalLayout({
                                      children,
                                      requireAuth,
                                      fallbackToLogin = true,
                                      minimalLayout = false,
                                      loadingContent,
                                      notFoundContent,
                                      isLoading = false,
                                      isNotFound = false,
                                  }: ConditionalLayoutProps) {
    const {isAuthenticated, loading: userLoading} = useUser()

    // Show loading state
    if (isLoading || userLoading) {
        return loadingContent || <DefaultLoadingContent/>
    }

    // Show not found state
    if (isNotFound) {
        return notFoundContent || <DefaultNotFoundContent/>
    }

    // Determine if authentication is required
    const authRequired = typeof requireAuth === 'function' ? requireAuth() : requireAuth

    // If authentication is not required, show content with appropriate layout
    if (!authRequired) {
        return minimalLayout ? (
            <MinimalLayout>{children}</MinimalLayout>
        ) : (
            <AuthenticatedLayout>{children}</AuthenticatedLayout>
        )
    }

    // Authentication is required but user is not authenticated
    if (!isAuthenticated) {
        if (fallbackToLogin) {
            // This will redirect to login via AuthenticatedLayout
            return <AuthenticatedLayout>{children}</AuthenticatedLayout>
        } else {
            // Show minimal layout without authentication
            return <MinimalLayout>{children}</MinimalLayout>
        }
    }

    // User is authenticated and authentication is required
    return <AuthenticatedLayout>{children}</AuthenticatedLayout>
}